import React, { useContext } from "react";

import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { DataContext } from "../../../../../providers/VacancyDataProvider";
import { getCategoryImage } from "../../../../pages/Vacancies/categoryImages";
import KeywordSearch from "../../../../pages/Vacancies/KeywordSearch";
import ProximityFilter from "../../../../pages/Vacancies/ProximityFilter";
import VacancyFilter from "../../../../pages/Vacancies/VacancyFilter";
import Icon from "../../../icons/Icon";
import LoadingIndicator from "../../../LoadingIndicator";
import Button from "../../../NewForms/Button";
import NewHeadline from "../../../NewHeadline";
import SmallCard from "../../../SmallCard";

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    justify-content: space-between;
`;

const Container = styled.div`
    ${({ theme }) => theme.breakpoints.from.small.css`
       ${theme.paddings.containerOuter.left}
       ${theme.paddings.containerOuter.right}
    `}

    min-height: 25vh;
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    ${({ theme }) => theme.paddings.containerInner.left}
    ${({ theme }) => theme.paddings.containerInner.right}
    ${({ theme }) => theme.breakpoints.from.small.css`
       padding: 0;
    `}
    ${({ theme }) => theme.breakpoints.from.medium.css`
       margin-bottom: 50px;
    `}
`;

const Inline = styled.div`
    display: flex;
    gap: 30px;
    align-content: end;
    flex-direction: column;
    flex-grow: 1;
    ${({ theme }) => theme.breakpoints.from.small.css`
      flex-direction: row;
      flex-grow: 0;
    `}
`;

const IconContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    column-gap: 15px;
    padding-top: 10px;
    ${({ theme }) => theme.breakpoints.from.small.css`
      column-gap: 30px;
      align-items: center;
    `}
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 40px;
`;

const InputContainer = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    min-width: 300px;
    ${({ theme }) => theme.breakpoints.from.small.css`
        min-width: 0;
        padding-top: 10px;
    `}
`;

const InteractableIcon = styled(Icon)`
    cursor: pointer;
`;

const List = ({ name }) => {
    const dataContext = useContext(DataContext);
    const theme = useTheme();
    const isMobile = theme.breakpoints.upTo.small.useMatches();

    const { slicedVacancies, preparedVacancies, loading, isActiveModal } =
        dataContext.data;
    const { handleSort, loadMore, setIsActiveModal, setSearchKeyword } =
        dataContext.actions;

    if (slicedVacancies === null || loading) {
        return (
            <Container>
                <LoadingIndicator />
            </Container>
        );
    }

    const handleKeywordSearch = (keyword) => {
        setSearchKeyword(keyword.toLowerCase());
    };

    return (
        <Container>
            <Row>
                <NewHeadline>{name}</NewHeadline>
                {isMobile && (
                    <IconBar
                        isActiveModal={isActiveModal}
                        setIsActiveModal={setIsActiveModal}
                        handleSort={handleSort}
                    />
                )}

                <Inline>
                    <InputContainer>
                        <KeywordSearch onChange={handleKeywordSearch} />
                        <ProximityFilter />
                    </InputContainer>
                    {!isMobile && (
                        <IconBar
                            isActiveModal={isActiveModal}
                            setIsActiveModal={setIsActiveModal}
                            handleSort={handleSort}
                        />
                    )}
                </Inline>
            </Row>
            <ListContainer>
                {slicedVacancies.length === 0 && (
                    <p>
                        Es konnten keine Stellenangebote für Deine Suchanfrage
                        gefunden werden.
                    </p>
                )}
                {slicedVacancies.length > 0 &&
                    slicedVacancies.map((vacancy) => {
                        const categoryImage = getCategoryImage(vacancy);
                        return (
                            <SmallCard
                                key={vacancy.id}
                                name={vacancy.external_posting_name}
                                subline={vacancy.geo_city}
                                link={{
                                    href: `/stellenangebote/${vacancy.id}`,
                                    text: "Mehr erfahren",
                                }}
                                src={{ publicURL: categoryImage }}
                            />
                        );
                    })}
            </ListContainer>
            {preparedVacancies.length > 4 &&
                slicedVacancies.length < preparedVacancies.length && (
                    <ActionRow>
                        <Button onClick={loadMore} color="redOutline">
                            Weitere laden
                        </Button>
                    </ActionRow>
                )}
        </Container>
    );
};

const IconBar = ({ isActiveModal, setIsActiveModal, handleSort }) => {
    const theme = useTheme();
    return (
        <IconContainer>
            <InteractableIcon
                iconName="filter"
                size="30px"
                sExtraSmall="25px"
                color={theme.colors.blue.toString()}
                hoverColor={theme.colors.red.toString()}
                handleClick={() => setIsActiveModal(!isActiveModal)}
            />
            <VacancyFilter />
            <InteractableIcon
                iconName="sort"
                size="30px"
                sExtraSmall="25px"
                color={theme.colors.blue.toString()}
                hoverColor={theme.colors.red.toString()}
                handleClick={handleSort}
            />
        </IconContainer>
    );
};

export default List;
